/**
 * Применение шаблона к форме задачи. Инварианты:
 *   - Отсутствующие в payload ключи не трогают форму (Q9).
 *   - isSummary не применяется: вычисляется по наличию subtasks.
 *   - assigneeId проходит soft-skip, если сотрудник уволен/не найден
 *     (Q4). Поля, исключённые из шаблона (start/deadline/isHourly/
 *     dependency*), в payload отсутствуют и форму не перетирают.
 *   - projectId применяется отдельной ветвью с учётом isProjectLocked.
 */
import { useCallback, useState } from "react";
import { applyTemplatePayload } from "../utils/templateSchemas";
import { collectTaskPayloads } from "../utils/templateNesting";

export function useTaskTemplate({
  isCopy,
  copySource,
  db,
  setFieldValue,
  isProjectLocked,
  effectiveProjectId,
  toast,
}) {
  const [appliedTemplateName, setAppliedTemplateName] = useState(null);

  const [pendingTemplateSubtasks, setPendingTemplateSubtasks] = useState(() =>
    isCopy ? collectTaskPayloads(db.tasks, copySource.id) : [],
  );

  const applyTemplate = useCallback(
    (template) => {
      if (!template) {
        setAppliedTemplateName(null);
        setPendingTemplateSubtasks([]);
        return;
      }

      const patch = applyTemplatePayload("task", template.payload);
      const {
        subtasks: nestedSubtasks,
        projectId: templateProjectId,
        ...taskFields
      } = patch;

      const safeTaskFields = { ...taskFields };

      if (safeTaskFields.assigneeId) {
        const emp = db.employees.find(
          (e) => e.id === safeTaskFields.assigneeId,
        );
        if (!emp || emp.fired) {
          toast(
            "Исполнитель из шаблона недоступен — выберите вручную",
            "warning",
          );
          delete safeTaskFields.assigneeId;
        }
      }

      Object.keys(safeTaskFields).forEach((field) => {
        if (field === "tags") {
          setFieldValue(
            "tagsInput",
            Array.isArray(safeTaskFields.tags)
              ? safeTaskFields.tags.join(", ")
              : "",
          );
          return;
        }
        if (field === "isSummary") return;
        setFieldValue(field, safeTaskFields[field]);
      });

      const cleanSubtasks = Array.isArray(nestedSubtasks) ? nestedSubtasks : [];
      setPendingTemplateSubtasks(cleanSubtasks);

      if (cleanSubtasks.length > 0) setFieldValue("isSummary", true);

      if (!templateProjectId) {
        setAppliedTemplateName(template.name);
        return;
      }

      if (isProjectLocked) {
        if (templateProjectId !== effectiveProjectId) {
          toast(
            "Проект из шаблона не применён: задача создаётся в фиксированном проекте",
            "info",
          );
          return;
        }
        setAppliedTemplateName(template.name);
        return;
      }

      const projectEntry = db.projects.find((p) => p.id === templateProjectId);
      const accessible =
        projectEntry &&
        projectEntry.status === "active" &&
        !projectEntry.archived;
      if (!accessible) {
        toast(
          "Проект из шаблона недоступен - выберите проект вручную",
          "warning",
        );
        return;
      }

      setFieldValue("projectId", templateProjectId);
      setAppliedTemplateName(template.name);
    },
    [setFieldValue, isProjectLocked, effectiveProjectId, db, toast],
  );

  return { appliedTemplateName, pendingTemplateSubtasks, applyTemplate };
}
